import type { MergedAccountView } from "@sales-ops-amplify/shared";
import {
  coalesceCompanyProfile,
  companyPrimaryFacts,
} from "@/lib/company-profile";

export function CompanyOverviewTab({
  merged,
  isMutable,
  openOpportunityCount,
  leadCoverageCount,
  researchFreshnessDays,
}: {
  merged: MergedAccountView;
  isMutable: boolean;
  openOpportunityCount?: number;
  leadCoverageCount?: number;
  researchFreshnessDays?: number | null;
}) {
  const profile = coalesceCompanyProfile(merged);
  const facts = companyPrimaryFacts(profile);
  const user = merged.user;
  const keywords = profile.keywords ?? [];
  const technologies = profile.technologies ?? [];
  const research = user?.research ?? merged.directory?.research;
  const researchSections = user?.researchSections;

  return (
    <div className="min-w-0 max-w-full space-y-4 overflow-x-hidden">
      {isMutable ? (
        <div className="grid grid-cols-3 gap-3">
          <HealthTile
            label="Open pipeline"
            value={String(openOpportunityCount ?? 0)}
          />
          <HealthTile
            label="Research age"
            value={
              researchFreshnessDays == null
                ? "—"
                : `${researchFreshnessDays}d`
            }
          />
          <HealthTile
            label="People"
            value={String(leadCoverageCount ?? 0)}
          />
        </div>
      ) : null}

      <section className="min-w-0 overflow-hidden rounded-lg border border-border bg-card p-4">
        <h3 className="text-sm font-semibold text-foreground">Company</h3>
        {profile.description ? (
          <p className="mt-3 break-words text-sm leading-relaxed text-muted-foreground">
            {profile.description}
          </p>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">No description</p>
        )}
        <dl className="mt-4 grid min-w-0 gap-3 sm:grid-cols-2">
          {facts.map((f) => (
            <div key={f.key} className="min-w-0">
              <dt className="text-[11px] uppercase text-muted-foreground">
                {f.label}
              </dt>
              <dd className="break-words text-sm">{f.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {(keywords.length > 0 ||
        technologies.length > 0 ||
        profile.latestFunding ||
        profile.annualRevenue ||
        profile.totalFunding) && (
        <section className="min-w-0 overflow-hidden rounded-lg border border-border bg-card p-4">
          <h3 className="text-sm font-semibold">Insights</h3>
          {keywords.length > 0 ? (
            <div className="mt-3 min-w-0">
              <p className="text-[11px] uppercase text-muted-foreground">
                Keywords
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {keywords.slice(0, 12).map((k) => (
                  <span
                    key={k}
                    className="max-w-full break-words rounded-md border border-border bg-muted/30 px-2 py-0.5 text-xs"
                  >
                    {k}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {technologies.length > 0 ? (
            <div className="mt-3 min-w-0">
              <p className="text-[11px] uppercase text-muted-foreground">
                Technologies
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {technologies.slice(0, 12).map((t) => (
                  <span
                    key={t}
                    className="max-w-full break-words rounded-md border border-border bg-muted/30 px-2 py-0.5 text-xs"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          <dl className="mt-4 grid min-w-0 gap-3 text-sm sm:grid-cols-2">
            <div className="min-w-0">
              <dt className="text-[11px] uppercase text-muted-foreground">
                Funding
              </dt>
              <dd className="break-words">
                {String(
                  user?.funding ??
                    profile.latestFunding ??
                    profile.totalFunding ??
                    "—",
                )}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[11px] uppercase text-muted-foreground">
                Revenue
              </dt>
              <dd className="break-words">
                {String(profile.annualRevenue ?? "—")}
              </dd>
            </div>
          </dl>
        </section>
      )}

      {researchSections && Object.keys(researchSections).length > 0 ? (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">
            Research {user?.research ? "(yours)" : ""}
          </h3>
          {Object.entries(researchSections).map(([key, body]) => (
            <div
              key={key}
              className="rounded-lg border border-border bg-card p-4"
            >
              <h4 className="mb-2 text-sm font-semibold capitalize">
                {key.replace(/_/g, " ")}
              </h4>
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words text-xs text-muted-foreground">
                {body}
              </pre>
            </div>
          ))}
        </section>
      ) : research ? (
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-2 text-sm font-semibold">
            Research {user?.research ? "(yours)" : "(directory)"}
          </h3>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words text-xs text-muted-foreground">
            {research}
          </pre>
        </section>
      ) : null}
    </div>
  );
}

function HealthTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3 text-center">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-xl font-bold text-foreground">{value}</p>
    </div>
  );
}
